import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import fileupload from "../../../assets/img/icons/fileupload.svg";
import Resizer from "react-image-file-resizer";
import { addCategory } from "store/actions/categoriesActions";
import { updateCategory } from "store/actions/categoriesActions";

const CategoryModal = ({
  isOpen,
  toggle,
  menuId,
  editMode,
  activeCategory,
}) => {
  const dispatch = useDispatch();
  const { user, userPermissions } = useSelector((state) => state.auth);
  const addAccess = user?.type === "restaurant" || userPermissions?.menus?.add;
  const editAccess =
    user?.type === "restaurant" || userPermissions?.menus?.edit;

  const fileInput = useRef(null);

  const [categoryName, setCategoryName] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [droppedFile, setDroppedFile] = useState(null);
  const [previousImageUrl, setPreviousImageUrl] = useState("");

  const handleImageClick = () => {
    fileInput.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
    const maxSizeInKB = 2048;

    if (!allowedExtensions.exec(file?.name)) {
      toast.error(
        "Invalid file type. Please upload only JPG, JPEG, or PNG images."
      );
      fileInput.current.value = "";
      return;
    }

    if (file.size > maxSizeInKB * 1024) {
      toast.warn("You can only upload a 2MB picture.");
    } else {
      try {
        Resizer.imageFileResizer(
          file,
          300,
          300,
          "JPEG",
          70,
          0,
          async (compressedImage) => {
            try {
              const response = await fetch(compressedImage);
              const compressedImageBlob = await response.blob();
              const compressedFile = new File(
                [compressedImageBlob],
                file.name,
                {
                  type: `image/${file.name.split(".")[1]}`,
                  lastModified: file.lastModified,
                  lastModifiedDate: file.lastModifiedDate,
                }
              );
              setCategoryImage(compressedFile);
            } catch (error) {
              toast.error("Failed to compress the image.");
              return;
            }
          },
          "base64"
        );
      } catch (error) {
        toast.error("Failed to compress the image.");
        return;
      }
    }

    setDroppedFile(null);
  };

  const handleDropCategory = (file) => {
    const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i;
    const maxSizeInKB = 2048;
    if (!allowedExtensions.exec(file?.name)) {
      toast.error(
        "Invalid file type. Please upload only JPG, JPEG or PNG images."
      );
      return;
    }
    if (file.size > maxSizeInKB * 1024) {
      toast.warn("You can only upload a 2MB picture.");
    } else {
      try {
        Resizer.imageFileResizer(
          file,
          300,
          300,
          "JPEG",
          70,
          0,
          async (compressedImage) => {
            try {
              const response = await fetch(compressedImage);
              const compressedImageBlob = await response.blob();
              const compressedFile = new File(
                [compressedImageBlob],
                file.name,
                {
                  type: `image/${file.name.split(".")[1]}`,
                  lastModified: file.lastModified,
                  lastModifiedDate: file.lastModifiedDate,
                }
              );
              setCategoryImage(compressedFile);

              setDroppedFile(compressedFile);
            } catch (error) {
              toast.error("Failed to compress the image.");
              return;
            }
          },
          "base64"
        );
      } catch (error) {
        toast.error("Failed to compress the image.");
        return;
      }
    }
  };

  const handleAddMenu = () => {
    if (!addAccess) {
      toast.error("You don't have access to perform this action");
      return;
    }
    setIsLoading(true);
    dispatch(
      addCategory(
        user?.restaurantID,
        menuId,
        categoryName?.trim(),
        categoryImage
      )
    )
      .then(() => toggle())
      .finally(() => setIsLoading(false));
  };

  const handleEditMenu = () => {
    if (!editAccess) {
      toast.error("You don't have access to perform this action");
      return;
    }
    setIsLoading(true);
    dispatch(
      updateCategory(
        activeCategory?.id,
        categoryName?.trim(),
        categoryImage,
        previousImageUrl
      )
    )
      .then(() => toggle())
      .finally(() => setIsLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editMode) {
      handleEditMenu();
    } else {
      handleAddMenu();
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (editMode && activeCategory) {
        setCategoryName(activeCategory.name || "");
        setCategoryImage(activeCategory.image || null);
        setPreviousImageUrl(activeCategory.image || "");
      }
    } else {
      setCategoryName("");
      setCategoryImage(null);
    }
  }, [isOpen, editMode, activeCategory]);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle} className="align-items-center border-bottom">
        {editMode ? "Edit" : "Add"} Category
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="categoryName">Category Name </Label>
            <Input
              id="categoryName"
              name="categoryName"
              placeholder="Pasta"
              type="text"
              required
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
            />
          </FormGroup>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleDropCategory(e.dataTransfer.files[0]);
            }}
          >
            {categoryImage ? (
              <div className="d-flex flex-column justify-content-center align-items-center border">
                <img
                  src={
                    categoryImage instanceof File
                      ? URL.createObjectURL(categoryImage)
                      : categoryImage
                  }
                  alt="Uploaded"
                  style={{
                    width: "200px",
                    height: "200px",
                    border: "1px dotted #dde2e7",
                  }}
                  className="cursor-pointer rounded m-1"
                  onClick={handleImageClick}
                />
                <i
                  className="fa-regular fa-pen-to-square w-100  py-2 shadow-sm cursor-pointer d-flex justify-content-center border"
                  onClick={handleImageClick}
                />
              </div>
            ) : (
              <div
                className={`placeholder-image ${
                  droppedFile ? "dropped" : ""
                } cursor-pointer`}
                style={{
                  width: "100px",
                  height: "100px",
                  border: "1px dotted #dde2e7",
                  padding: "10px",
                  margin: "0 auto",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "12px",
                }}
                onClick={handleImageClick}
              >
                {droppedFile ? (
                  <img
                    src={URL.createObjectURL(droppedFile)}
                    alt="Dropped"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <img
                    src={fileupload}
                    alt="Placeholder"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </div>
            )}
            <input
              type="file"
              ref={fileInput}
              accept=".jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
          <p className="text-muted mt-3">
            Recommended resolution is for landscape 1536x1024px, square
            1536x1536px or portrait 1536x2304 or bigger with a file size of less
            than 10MB.
          </p>
        </ModalBody>
        <ModalFooter className="border-top py-3">
          <Button
            type="submit"
            color="primary"
            className="d-flex align-items-center"
            disabled={isLoading || !categoryName?.trim() || !categoryImage}
          >
            {isLoading && <Spinner size="sm" className="mr-2" />}
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CategoryModal;
